import type { SecurityControlMetrics } from "@app/deploy/security-controls";
import {
  IconCheckCircle,
  IconExclamationCircle,
  IconMinusCircle,
} from "@app/ui/shared/icons";
import cn from "classnames";
import { RadialProgress } from "./radial-progress";

export interface ComplianceSummaryProps {
  metrics: SecurityControlMetrics;
  isLoading?: boolean;
  className?: string;
}

export const ComplianceSummary = ({
  metrics,
  isLoading = false,
  className = "",
}: ComplianceSummaryProps) => {
  const percentage = metrics.progressPercentage;
  const isNoScope = metrics.status === "NO_SCOPE";

  if (isLoading) {
    return (
      <div className={cn("flex min-w-0 items-center gap-3", className)}>
        <div className="size-10 shrink-0 animate-pulse rounded-full bg-gray-200" />
        <div className="min-w-0 flex flex-col gap-1">
          <div className="h-3.5 w-10 animate-pulse rounded bg-gray-200" />
          <div className="h-3 w-20 animate-pulse rounded bg-gray-100" />
        </div>
      </div>
    );
  }

  // Determine colors and badge based on percentage
  let badgeBgColor: string;
  let percentageColor: string;
  let badgeContent: React.ReactNode;

  if (isNoScope) {
    badgeBgColor = "bg-gray-100";
    percentageColor = "text-gray-500";
    badgeContent = (
      <IconMinusCircle className="size-6 shrink-0 text-gray-500" />
    );
  } else if (percentage === 0) {
    badgeBgColor = "bg-red-100";
    percentageColor = "text-red-500";
    badgeContent = (
      <IconExclamationCircle color="#EF4444" className="size-6 shrink-0" />
    );
  } else if (percentage === 100) {
    badgeBgColor = "bg-emerald-100";
    percentageColor = "text-forest";
    badgeContent = (
      <IconCheckCircle color="#00633F" className="size-6 shrink-0" />
    );
  } else {
    // 1-99%
    badgeBgColor = "bg-amber-100";
    percentageColor = "text-amber-700";
    badgeContent = (
      <RadialProgress
        progress={percentage}
        radius={18}
        strokeWidth={3}
        variant="warn"
        className="shrink-0"
      />
    );
  }

  return (
    <div className={cn("flex min-w-0 items-center gap-3", className)}>
      <div
        className={cn(
          "flex size-10 shrink-0 items-center justify-center self-center rounded-full aspect-square",
          badgeBgColor,
        )}
      >
        {badgeContent}
      </div>
      <div className="min-w-0 flex flex-col">
        <span
          className={cn("text-sm font-medium", percentageColor)}
          style={
            percentage === 0 && !isNoScope
              ? { color: "#EF4444" }
              : percentage === 100 && !isNoScope
                ? { color: "#00633F" }
                : undefined
          }
        >
          {isNoScope ? "--" : `${percentage}%`}
        </span>
        <span className="text-xs text-gray-600">
          {isNoScope ? "No in-scope resources" : metrics.implementationSummary}
        </span>
      </div>
    </div>
  );
};
